// De telling achter de pagina 'Huishoudens per status': hoeveel huishoudens er
// nu in elke status staan, hoeveel er in een gekozen periode in kwamen, en
// hoeveel er in die periode werden afgerond, per dienst.
//
// Nodig omdat de dashboardkaart 'Status' (useAddressStatusCounts) alleen de
// stand van NU kent; een periode vraagt de statusgeschiedenis (StatusHistory).
//
// 'Geholpen' = in de periode op 'Afgerond' (completed) gekomen (besluit Gijs
// 2026-10-13). Per dienst telt de dienst die het huishouden NU draagt.
//
// AI-CONTEXT: See docs/features/huishoudens-status-per-periode.md

import toMillis from '../measurementSignals/toMillis'
import { ADRESS_STATUS_KEYS } from './adressStatusLabels'
import { dienstKeuzes, dienstenOf } from '../wervingsacties/resolveWervingsDiensten'

const AFGEROND = 'completed'

/**
 * @typedef {object} StatusRegel
 * @property {string} status - Statussleutel uit ADRESS_STATUS_KEYS
 * @property {number} nu - Huishoudens die nu deze status hebben
 * @property {number} inPeriode - Unieke huishoudens die in de periode op deze status kwamen
 */

/**
 * @typedef {object} DienstRegel
 * @property {string} key - Dienstsleutel
 * @property {string} label - Naam van de dienst
 * @property {number} aantal - In de periode afgeronde huishoudens met deze dienst
 */

/**
 * @typedef {object} StatusPeriodSummary
 * @property {Array<StatusRegel>} perStatus - Eén regel per status, in de volgorde van het traject
 * @property {{ totaal: number, zonderDienst: number, perDienst: Array<DienstRegel> }} afgerond - Afgerond in de periode; het totaal telt elk huishouden één keer
 * @property {number} afgerondZonderDatum - Nu 'Afgerond' zonder enige afrondingsdatum in de geschiedenis; tellen in geen periode mee
 * @property {{ nu: number, inPeriode: number }} totaal - Nu: huishoudens met een status; in de periode: unieke huishoudens met een statuswissel (elk één keer)
 */

/**
 * Lokale middernacht van een `yyyy-mm-dd`, of van de dag erna.
 * Via `new Date(y, m, d + 1)` en niet `+ 86400000`: bij de zomertijdwissel is
 * een dag 23 of 25 uur.
 *
 * @param {string} dag
 * @param {number} [plusDagen=0]
 * @returns {number | null}
 */
const middernacht = (dag, plusDagen = 0) => {
    const ms = toMillis(dag)
    if (ms === null) return null
    const d = new Date(ms)
    return new Date(d.getFullYear(), d.getMonth(), d.getDate() + plusDagen).getTime()
}

/**
 * @param {object} params
 * @param {Array<object>} params.adressen - De levende huishoudens (Adresses)
 * @param {Array<object>} params.history - StatusHistory-regels
 * @param {string} params.from - Eerste dag van de periode, `yyyy-mm-dd`, telt helemaal mee
 * @param {string} params.to - Laatste dag van de periode, `yyyy-mm-dd`, telt helemaal mee
 * @param {Array<{ key: string, label: string }>} params.diensten - Zichtbare diensten (resolveWervingsDiensten)
 * @param {Object<string, string>} params.dienstNamen - Sleutel → naam, ook verborgen (dienstNamen)
 * @returns {StatusPeriodSummary}
 */
const buildStatusPeriodSummary = ({ adressen, history, from, to, diensten, dienstNamen }) => {
    const levend = new Map((adressen || []).map((a) => [a.ID, a]))
    const begin = middernacht(from)
    const eind = middernacht(to, 1)

    // Alleen regels van huishoudens die er nog zijn: een verwijderd huishouden
    // telt nergens anders mee, dus hier ook niet.
    const regels = (history || []).filter((r) =>
        r && r.Deleted !== true && r.EntityType === 'adress' && levend.has(r.EntityID))

    /** status → Set van huishouden-ID's die er in de periode op kwamen */
    const inPeriode = new Map(ADRESS_STATUS_KEYS.map((s) => [s, new Set()]))
    /** huishoudens met ooit een gedateerde afronding */
    const ooitAfgerond = new Set()

    for (const r of regels) {
        const ms = toMillis(r.ChangedAt)
        if (ms === null) continue
        if (r.ToStatus === AFGEROND) ooitAfgerond.add(r.EntityID)
        if (begin !== null && eind !== null && ms >= begin && ms < eind && inPeriode.has(r.ToStatus)) {
            inPeriode.get(r.ToStatus).add(r.EntityID)
        }
    }

    const perStatus = ADRESS_STATUS_KEYS.map((status) => ({
        status,
        nu: [...levend.values()].filter((a) => a.Status === status).length,
        inPeriode: inPeriode.get(status).size,
    }))

    const afgerondeHuizen = [...inPeriode.get(AFGEROND)].map((id) => levend.get(id))
    const gebruikt = afgerondeHuizen.flatMap(dienstenOf)
    const perDienst = dienstKeuzes(diensten || [], gebruikt, dienstNamen).map((d) => ({
        key: d.key,
        label: d.label,
        aantal: afgerondeHuizen.filter((a) => dienstenOf(a).includes(d.key)).length,
    }))

    // Elk huishouden één keer: een huishouden dat in de periode door twee
    // statussen ging, staat in twee kolomcellen maar telt hier één keer
    // (testronde 2026-10-13, R1-B3).
    const inPeriodeUniek = new Set()
    inPeriode.forEach((ids) => ids.forEach((id) => inPeriodeUniek.add(id)))

    return {
        perStatus,
        totaal: {
            nu: perStatus.reduce((som, s) => som + s.nu, 0),
            inPeriode: inPeriodeUniek.size,
        },
        afgerond: {
            totaal: afgerondeHuizen.length,
            zonderDienst: afgerondeHuizen.filter((a) => dienstenOf(a).length === 0).length,
            perDienst,
        },
        afgerondZonderDatum: [...levend.values()]
            .filter((a) => a.Status === AFGEROND && !ooitAfgerond.has(a.ID)).length,
    }
}

export default buildStatusPeriodSummary
